import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeftIcon, ChevronDownIcon, ChevronRightIcon, CheckCircleIcon,
  PaperClipIcon, ArrowDownTrayIcon, TrashIcon, ArrowUturnUpIcon, ChatBubbleLeftRightIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import {
  useAbsenceReportingSubmissions,
  useUpdateAbsenceReportingSubmission,
  useArchiveAbsenceReportingSubmission,
  useUnarchiveAbsenceReportingSubmission,
  useSubmitterMessages,
  useSendSubmitterMessage,
} from '../hooks/useAbsenceReporting';
import { useDirectory } from '../hooks/useDirectory';
import { categoryBySlug, REPORT_TYPE_LABEL } from './absenceReportingCategories';
import DocumentViewer from './DocumentViewer';
import EmployeeLookup, { employeeOptionsFromDirectory } from './EmployeeLookup';
import AbsenceRecordEditor from './AbsenceRecordEditor';


// Still reachable as its own category page (AbsenceReportingCategory.jsx)
// too, linked from the header — that page is where a manager actually
// confirms-and-acts on one, same as before; the lists below are the
// at-a-glance view.
const RETURN_TO_WORK = categoryBySlug('return-to-work');

// The three states these lists track. Order fixed: Absent first (the more
// common/urgent one), Late second, Returned third — a completed Return to
// Work is good news, not something needing the same urgency.
const LIST_TYPES = [
  { reportType: 'Report Absence', label: 'Absent', icon: categoryBySlug('absence-reporting').icon },
  { reportType: 'Report Late', label: 'Late', icon: categoryBySlug('report-late').icon },
  { reportType: 'Return to Work', label: 'Returned', icon: RETURN_TO_WORK.icon },
];

const TODAY_EMPTY_TEXT = {
  'Report Absence': 'No one absent today.',
  'Report Late': 'No one late today.',
};

// Sentinel, not a real report_type — "no filter applied", Today's default.
const ALL_TYPE = 'all';
// Today is absences and lateness only — someone who's back is in Returned,
// nowhere else.
const TODAY_TABS = [{ reportType: ALL_TYPE, label: 'All' }, ...LIST_TYPES.filter((t) => t.reportType !== 'Return to Work')];

function categoryForRow(row) {
  return LIST_TYPES.find((t) => t.reportType === row.report_type);
}

function matchesType(row, type) {
  if (type === ALL_TYPE) return row.report_type === 'Report Absence' || row.report_type === 'Report Late';
  return row.report_type === type;
}

// Back at work: a Return to Work closed it (returned_to_work_at), a manager
// said they're back but the form isn't filed yet (return_pending_at), or one
// of the handful of old rows filed as a Return to Work of its own. These are
// shown in Returned, and in no other section.
function isReturned(row) {
  return row.report_type === 'Return to Work' || Boolean(row.returned_to_work_at) || Boolean(row.return_pending_at);
}

// When they came back, for sorting and the Returned date filter.
function returnedAt(row) {
  return row.returned_to_work_at || row.return_pending_at || row.submitted_at;
}

// Pending: an open absence nobody's confirmed today — no Still Absent tap,
// no return — so HR is waiting to hear whether they're still off or back.
// Filing it today counts as today's confirmation, same as FSM's own
// confirmedToday.
function isPendingAbsence(row) {
  return row.report_type === 'Report Absence' && !isReturned(row) && !row.archived_at && !row.superseded_at
    && !isToday(row.last_confirmed_at || row.event_at || row.submitted_at);
}

// The last time something happened that HR needs to acknowledge again:
// filed or edited, or a Still Absent. An Acknowledged tick made before this
// is stale. A return (Returned — pending form, or the Return to Work filed)
// deliberately doesn't count — it leaves both ticks as they are.
function lastStatusChange(row) {
  return Math.max(
    ...[row.submitted_at, row.last_confirmed_at]
      .filter(Boolean)
      .map((d) => new Date(d).getTime())
  );
}

const ACK_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'not-acknowledged', label: 'Not acknowledged' },
];

function matchesAck(row, filter, isAcknowledged) {
  if (filter === 'all') return true;
  return filter === 'acknowledged' ? isAcknowledged(row) : !isAcknowledged(row);
}

function fmtTime(iso) {
  return iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—';
}

// dd/MM/yyyy — all numbers, same as the dates picked on the forms.
function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/London' }) : '—';
}

// The day they were actually back at work, for a closed absence: the
// Absence End Date picked on the Return to Work (stored as dd/MM/yyyy text —
// the form's own Days Absent treats it as the day they came back), else the
// day the Return to Work was filed. Never returned_to_work_at alone when an
// end date was picked — that's when the form was filed, which can be days
// after they came back. Same format as fmtDate, so the two compare; null
// while still open.
function backAtWorkDate(row) {
  if (!row.returned_to_work_at) return null;
  const picked = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(row.absence_end_date || '');
  if (!picked) return fmtDate(row.returned_to_work_at);
  return fmtDate(new Date(Date.UTC(Number(picked[3]), Number(picked[2]) - 1, Number(picked[1]), 12)).toISOString());
}

// ---- Excel export: every date and time in its own clearly named column ----
// dd/MM/yyyy and HH:mm, UK time, blank rather than "—" when there's nothing
// (a blank cell reads as "not yet" in a spreadsheet, a dash as data).
function xDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/London' }) : '';
}
const exportDayMs = (text) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text || '');
  return m ? Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
};
// When it happened: the Return to Work's picked Absence Start Date, else an
// edit's corrected date (event_at), else when it was reported.
function absenceDateText(row) {
  return row.absence_start_date || xDate(row.event_at || row.submitted_at);
}
// The day they were back — the picked Absence End Date, else the day the
// Return to Work was filed. Blank while still absent.
function returnedDateText(row) {
  if (!row.returned_to_work_at) return '';
  return row.absence_end_date || xDate(row.returned_to_work_at);
}
// Same count as the Return to Work form: from the start date up to, not
// including, the day they came back. Still absent: up to, not including,
// today — the same count the Pending list shows — marked "so far".
// Report Absence only.
function daysAbsentText(row) {
  if (row.report_type !== 'Report Absence') return '';
  const start = exportDayMs(absenceDateText(row));
  if (start === null) return '';
  const back = exportDayMs(returnedDateText(row));
  if (back !== null) return back >= start ? String(Math.round((back - start) / 86400000)) : '';
  const today = exportDayMs(xDate(new Date().toISOString()));
  return `${Math.max(0, Math.round((today - start) / 86400000))} so far`;
}

// The same count for reading on screen: "3 days", "1 day so far".
function daysAbsentLabel(row) {
  const text = daysAbsentText(row);
  if (!text) return null;
  const [n, ...rest] = text.split(' ');
  return `${n} day${n === '1' ? '' : 's'}${rest.length ? ` ${rest.join(' ')}` : ''}`;
}

// "Messaged by Jo Bloggs (email) 30 Sep 2026 09:12" — the latest follow-up
// sent about this record (FSM's absence_reporting_messages), so a second
// person doesn't chase the same thing without knowing it's been done.
function messageNote(row) {
  if (!row.last_message_at) return null;
  const how = row.last_message_channel === 'email' ? 'email' : 'Nano message';
  const more = row.message_count > 1 ? ` · ${row.message_count} sent` : '';
  return `Messaged by ${row.last_message_by || '—'} (${how}) ${fmtDate(row.last_message_at)} ${fmtTime(row.last_message_at)}${more}`;
}

// Today/All Records export: one row shape, and what each column means (the
// "Column guide" sheet).
const SUBMISSION_EXPORT_COLUMNS = [
  ['Employee', 'Who the record is about.'],
  ['Employee Email', 'Their email address.'],
  ['Department/Area', 'As picked on the Report Absence or Report Late form (e.g. Region N).'],
  ['Area', 'As picked on the form (e.g. Engineering - Southampton).'],
  ['Type', 'Report Absence or Report Late.'],
  ['Absence / Late Date', 'The day the absence started, or the day they were late. Uses the Absence Start Date picked on the Return to Work if there is one, otherwise the corrected date if the record was edited, otherwise the day it was reported.'],
  ['Time Employee Left Site/Work', 'Report Absence only: the time they left site or work, as entered on the Report Absence form. "Absent from start of day" when there is no time.'],
  ['Late Arrival Time', 'Report Late only: the time they arrived.'],
  ['Returned to Work Date', 'The day they came back to work: the Absence End Date picked on the Return to Work. Blank while still absent.'],
  ['Days Absent', 'Report Absence only. From the Absence / Late Date up to (not including) the Returned to Work Date, the same count as the Return to Work form. While still absent: days so far, up to (not including) today — the same count as the Pending list.'],
  ['Notes', 'Reason for absence, or reason for being late.'],
  ['Reported By', 'Who filed the report.'],
  ['Reported By Email', 'Their email address.'],
  ['Record Saved Date', 'The day this record was saved in the system — filed, or last edited. Not when the absence happened.'],
  ['Record Edited', 'Yes if this is an edited (amended) record — Record Saved Date is then the day it was edited.'],
  ['Return to Work Form Filed Date', 'The day the Return to Work form was filled in. Can be later than the Returned to Work Date. Blank if not filed yet.'],
  ['Acknowledged by HR', 'Yes once HR has ticked it on this dashboard.'],
  ['Recorded in BrightHR', 'Yes once HR has ticked Recorded in BrightHR on this dashboard.'],
];

function saveBlob(name, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// exceljs is a heavy dependency (~1MB) — loaded only once someone actually
// presses Export, same lazy-import convention as IspStockCounts.jsx's own
// Excel export. One plain sheet: a bold grey header row plus whatever rows
// the caller already filtered down to — each gallery exports exactly the
// rows it's currently showing, not the whole dataset behind it.
//
// `columns` is either plain header strings or [header, what it means] pairs;
// with meanings, a second "Column guide" sheet spells out what each column
// holds — which date is which was the question HR kept having to ask.
async function exportToExcel(fileName, sheetTitle, columns, rows) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'NEO2';
  const ws = wb.addWorksheet(sheetTitle);
  const headers = columns.map((c) => (Array.isArray(c) ? c[0] : c));
  const header = ws.addRow(headers);
  header.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
  });
  rows.forEach((r) => ws.addRow(r));
  ws.columns.forEach((col) => {
    const lengths = col.values.filter(Boolean).map((v) => String(v).length);
    col.width = Math.min(60, Math.max(12, ...lengths) + 2);
  });
  const guide = columns.filter(Array.isArray);
  if (guide.length) {
    const gs = wb.addWorksheet('Column guide');
    const gh = gs.addRow(['Column', 'What it means']);
    gh.eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
    });
    guide.forEach(([name, meaning]) => gs.addRow([name, meaning]));
    gs.getColumn(1).width = Math.max(14, ...guide.map(([name]) => name.length)) + 2;
    gs.getColumn(2).width = 100;
    gs.getColumn(2).alignment = { wrapText: true, vertical: 'top' };
  }
  const buf = await wb.xlsx.writeBuffer();
  saveBlob(fileName, new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  }));
}

function exportFileName(label) {
  const today = new Date().toISOString().slice(0, 10);
  return `absence-reporting-${label}-${today}.xlsx`;
}

// The Report Absence form only asks "Time Employee left site/work" when
// Absence Reporting Time is "Throughout Day" (they came in, then left).
// Any absence without that time reads as off from the start of the day
// rather than leaving the line out. Raw HH:mm text from the form, like
// arrival time.
function leftSiteLine(row) {
  return row.time_employee_left_site ? `Time left site/work: ${row.time_employee_left_site}` : 'Absent from start of day';
}

// A Report Late row an engineer has Dismissed on FSM (not a genuine
// lateness, or already handled there) shouldn't surface here — Confirmed
// and still-pending ones both still show; only Dismiss opts a row out. See
// nano-fibre-fsm's AbsenceReportingList.jsx Confirm/Dismiss quick-action,
// which is what actually sets late_status.
// The day they were late: an edit's corrected date (event_at), else the day
// it was reported — the Late section's order and date filter, and the same
// date its export's Absence / Late Date shows.
function lateAt(row) {
  return row.event_at || row.submitted_at;
}

function isDismissedLate(row) {
  return row.report_type === 'Report Late' && row.late_status === 'dismissed';
}

const ukDay = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d);

// "Today" is the UK calendar day, whatever timezone the browser is in —
// the same day boundary FSM's 8am-UK van checks run on.
function isToday(iso) {
  if (!iso) return false;
  return ukDay(new Date(iso)) === ukDay(new Date());
}

// FSM's own AbsenceReportingList.jsx now bumps last_confirmed_at on the
// same open Report Absence row for "Still Absent" instead of inserting a
// new one each day, so submitted_at alone no longer says "still absent as
// of today" past day 1 — last_confirmed_at (when present) does. Report
// Late/Return to Work rows aren't re-confirmed in place like this, so they
// still go by submitted_at.
function todayAnchor(row) {
  return row.report_type === 'Report Absence' ? (row.last_confirmed_at || row.submitted_at) : row.submitted_at;
}

// Inclusive on both ends — `to` is a date-only string from an <input
// type="date">, so it's pushed to end-of-day or a same-day range would
// match nothing.
function inDateRange(iso, from, to) {
  if (!iso) return false;
  const d = new Date(iso);
  if (from && d < new Date(`${from}T00:00:00`)) return false;
  if (to && d > new Date(`${to}T23:59:59`)) return false;
  return true;
}

// Editing an already-filed record from here — clicking the row opens
// AbsenceRecordEditor.jsx right on this page (FSM's own questions, saved
// through FSM's amendSubmission): saving creates a new, amended entry with a
// mandatory reason rather than overwriting this row. Only meaningful for a
// Report Absence/Report Late. A row that's already superseded has already
// been replaced — unlike FSM's own list, this app's Today/Returned still
// shows a superseded row (for the audit trail, see its own "Edited"
// banner), so it needs its own explicit exclusion here rather than relying
// on already being filtered out of view.
function isAmendable(row) {
  return typeof row.id === 'number' && !row.superseded_at
    && (row.report_type === 'Report Absence' || row.report_type === 'Report Late');
}

// One of these per gallery (see AbsenceReportingDashboard below), each with
// its own state — picking a name in one only narrows that gallery, not the
// other two. Rendered inside the section it filters rather than once in the
// page header, so the control never sits apart from what it affects.
//
// This app had no collapsible section chrome anywhere before — every
// gallery was always fully expanded. Needed now that there's an Archived
// section nobody wants competing for screen space with the three that
// actually need daily attention (see AbsenceReportingDashboard's own
// openSections: Pending/Today/All default open, Archived defaults closed).
// Same layout as FSM's own CollapsibleSection component (chevron left of
// the title, badge on the right), kept local here rather than shared across apps for one component.
function Collapsible({ title, description, badge, open, onToggle, children }) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-3 px-3 py-3">
        <SectionToggle title={title} description={description} open={open} onToggle={onToggle} />
        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">{badge}</span>
      </div>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </section>
  );
}

// The title half of FSM's CollapsibleSection: chevron to the left of the
// title (right when closed, down when open), with the title itself as the
// click target. Used on its own by the three sections that carry
// filters/export in their header, so those controls sit beside the toggle
// rather than inside its click target.
function SectionToggle({ title, description, open, onToggle }) {
  return (
    <button type="button" onClick={onToggle} className="flex min-w-0 items-start gap-1.5 text-left">
      {open ? (
        <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      ) : (
        <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      )}
      <span>
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-xs text-gray-400">{description}</p>}
      </span>
    </button>
  );
}

function ExportButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:border-brand hover:text-brand"
    >
      <ArrowDownTrayIcon className="h-3.5 w-3.5" />
      {label || 'Export to Excel'}
    </button>
  );
}

function TypeTabs({ active, onChange }) {
  return (
    <div className="flex gap-1 border-b border-gray-200">
      {TODAY_TABS.map((t) => {
        const isActive = active === t.reportType;
        return (
          <button
            key={t.reportType}
            type="button"
            onClick={() => onChange(t.reportType)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-xs font-semibold transition ${
              isActive ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// The record's own attachment(s), one per column rather than side by side
// in one — a plain absence/late report always, plus a separate Return to
// Work report once returned_to_work_at is set (never merged into one
// document — see FSM's absenceReportPdf.js `section` param: a Return to
// Work is never asked on the Report Absence/Report Late form, so its
// answers don't belong on that form's own report).
// Fixed width either way so the columns to the right don't visibly shift
// row to row.
// Labelled buttons, not bare icons — this table has room to spare, so
// there's no reason to make someone hover a paperclip to find out what it
// opens.
// Icon-only, not a labelled button — was w-40 with a text label each, which
// between the three of these ate most of the row's width. Shrunk to make
// room for Notes in the middle (see Row's own layout); title/aria-label
// carry the same label a sighted mouse-user would otherwise read off the
// button text.
function AbsenceLateReportButton({ id, reportType, onView }) {
  if (typeof id !== 'number') return <span className="h-9 w-9 shrink-0" aria-hidden="true" />;
  const label = REPORT_TYPE_LABEL[reportType] || 'Report';
  return (
    <button
      type="button"
      onClick={() => onView(`/api/absence-reporting/${id}/report.pdf`, label)}
      title={label}
      aria-label={label}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:border-brand hover:text-brand"
    >
      <PaperClipIcon className="h-4 w-4" />
    </button>
  );
}

// Strictly the Return to Work report — a record without one (still open, or
// not a Report Absence at all) leaves this column empty rather than
// borrowing the absence/late attachment, so the two columns always mean the
// same specific thing on every row.
function RtwReportButton({ id, returnedToWorkAt, onView }) {
  if (typeof id !== 'number' || !returnedToWorkAt) return <span className="h-9 w-9 shrink-0" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={() => onView(`/api/absence-reporting/${id}/report.pdf?section=returnToWork`, 'Return to Work Report')}
      title="Return to Work Report"
      aria-label="Return to Work Report"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-100"
    >
      <PaperClipIcon className="h-4 w-4" />
    </button>
  );
}

// One row shared by both lists — name/reported-by plus an acknowledge
// toggle and the record's report attachment(s), condensed to labelled
// buttons so a manager can scan a lot of rows at a glance.
// Set on FSM's Return to Work prompt by picking "Later" (AbsenceReportingList.jsx's
// handleReturnToWorkLater) — the manager has confirmed the employee is
// actually back, they just haven't filled in the Return to Work form yet.
// Only meaningful on a still-open Report Absence; cleared the moment the
// form is actually submitted (FSM sets return_pending_at to NULL in the
// same write that sets returned_to_work_at), so the two flags never both
// read true at once.
function isPendingReturn(row) {
  return row.report_type === 'Report Absence' && Boolean(row.return_pending_at) && !row.returned_to_work_at;
}

// Column widths shared by every list's header and rows, so the details line
// up down the page. Each Dates line and Days value stays on one line (sized
// for the longest: "Time left site/work: 19:40", "99 days so far"). A
// two-word Category ("Unauthorised absence") wraps at the space, keeping it
// narrow so Notes gets the room; Notes wraps as needed. Category / Dates /
// Notes only from md: up — on a phone there's no room beside the name.
// Days stays, as it always has on Pending.
const COLS = {
  // Width set per list from its longest name (--name-col, see
  // nameColumnWidth) so every full name fits on one line.
  employee: 'min-w-0 flex-1 md:w-[var(--name-col,14rem)] md:flex-none',
  category: 'hidden w-[5.25rem] shrink-0 md:block',
  dates: 'hidden w-40 shrink-0 md:block',
  // Width set per list from its longest value (--days-col, see
  // daysColumnWidth), so "1 day" doesn't leave a gap before Notes.
  days: 'w-[var(--days-col,5.5rem)] shrink-0',
  notes: 'hidden min-w-0 flex-1 md:block',
  // the Late list's Notes, shown at every width (see DetailCells)
  reason: 'min-w-0 flex-1',
  // the Late list's Department/Area and Area, one under the other
  department: 'hidden w-44 shrink-0 md:block',
};

// The Employee column's width for one list: its longest name as rendered
// (text-sm, font-medium), measured on a canvas, never under 14rem. Set as
// the --name-col CSS variable on the list so the header and every row share
// it. Whatever's left over goes to Notes.
let measureCanvas;
function nameColumnWidth(names) {
  measureCanvas = measureCanvas || document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  ctx.font = `500 14px ${getComputedStyle(document.body).fontFamily}`;
  const longest = Math.max(0, ...names.map((n) => ctx.measureText(n || '').width));
  return `${Math.max(224, Math.ceil(longest) + 8)}px`;
}

// The Days column's width for one list, the same way: its longest value as
// rendered (text-xs, font-semibold), never under the "Days" heading.
function daysColumnWidth(values) {
  measureCanvas = measureCanvas || document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  ctx.font = `600 12px ${getComputedStyle(document.body).fontFamily}`;
  const longest = Math.max(0, ...values.map((v) => ctx.measureText(v || '').width));
  return `${Math.max(32, Math.ceil(longest) + 20)}px`;
}

// The detail columns of one row — Category, Dates, Days, Notes. Empty cells
// show "—" so a missing value still reads as a column, not a gap.
// absence_category needs FSM's list to include it (its LIST_COLUMNS).
// absenceColumns false: no Category or Days — the Late list, where neither
// the absence category nor days absent ever applies. Its Notes (the reason
// they were late) is then headed Reason and shown at every width — with
// the two columns gone there's room for it even on a phone. It also shows
// Department / Area in their place: the form's Department/Area and Area
// answers (departmentLines), one under the other.
function DetailCells({
  category, dateLines, days, notes, departmentLines, absenceColumns = true,
}) {
  const dates = (dateLines || []).filter(Boolean);
  const department = (departmentLines || []).filter(Boolean);
  return (
    <>
      {absenceColumns ? (
        <p className={`${COLS.category} whitespace-normal text-xs font-medium text-gray-700`}>{category || '—'}</p>
      ) : (
        <div className={`${COLS.department} space-y-1 text-xs text-gray-700`}>
          {department.length ? department.map((line) => <p key={line}>{line}</p>) : <p>—</p>}
        </div>
      )}
      <div className={`${COLS.dates} space-y-1 text-xs text-gray-600`}>
        {dates.length ? dates.map((line) => <p key={line} className="whitespace-nowrap">{line}</p>) : <p>—</p>}
      </div>
      {/* Amber while still open ("so far"), as Pending always showed it. */}
      {absenceColumns && (
        <p className={`${COLS.days} whitespace-nowrap text-xs font-semibold ${days?.includes('so far') ? 'text-amber-700' : 'text-gray-700'}`}>
          {days || ''}
        </p>
      )}
      <p className={`${absenceColumns ? COLS.notes : COLS.reason} whitespace-normal text-xs text-gray-600`}>{notes || '—'}</p>
    </>
  );
}

// The headings over DetailCells, in the same widths — and the same gap-2 as
// the rows' own column container.
function DetailHeadings({ absenceColumns = true }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <span className={COLS.employee}>Employee</span>
      {absenceColumns ? <span className={COLS.category}>Category</span> : <span className={COLS.department}>Department / Area</span>}
      <span className={COLS.dates}>Dates</span>
      {absenceColumns && <span className={COLS.days}>Days</span>}
      {absenceColumns ? <span className={COLS.notes}>Notes</span> : <span className={COLS.reason}>Reason</span>}
    </div>
  );
}

function Row({
  row, showDate, displayName, acknowledged, onToggleAcknowledge, brightHrRecorded, onToggleBrightHr, onViewReport,
  originalRecord, amendedIntoRecord, onDiscard, onUnarchive, onEdit, absenceColumns = true,
}) {
  const category = categoryForRow(row);
  const pendingReturn = isPendingReturn(row);
  const sentNote = messageNote(row);
  const isAbsence = row.report_type === 'Report Absence';
  // Someone tapped "Still Absent" on FSM today (it sets last_confirmed_at on
  // the open absence) — they're confirmed still off, not just reported.
  const stillAbsentToday = isAbsence && !row.returned_to_work_at && !pendingReturn && isToday(row.last_confirmed_at);
  // absence_start_date/employee_time_of_arrival are stored as the raw
  // dd/MM/yyyy or HH:mm text the form collected (see schema.sql), not ISO —
  // shown as-is rather than through fmtDate/fmtTime, which both expect ISO.
  //
  // Picked dates always win over automatic ones. Since: the Return to
  // Work's picked Absence Start Date once it exists, else an amend's
  // corrected date (event_at), else when it was reported (submitted_at —
  // without that fallback a never-edited record showed no absence date at
  // all). Returned: the day they were back (backAtWorkDate — the picked
  // Absence End Date); returned_to_work_at is only when the form was filed,
  // shown after it only when that's a different day (a form filed today
  // for an absence that ended last week).
  const dateLines = isAbsence
    ? [
        leftSiteLine(row),
        `Since ${row.absence_start_date || fmtDate(row.event_at || row.submitted_at)}`,
        row.returned_to_work_at ? `Returned ${backAtWorkDate(row)}` : null,
        // On its own line, so the row just grows taller.
        row.returned_to_work_at && backAtWorkDate(row) !== fmtDate(row.returned_to_work_at)
          ? `(form filed ${fmtDate(row.returned_to_work_at)})`
          : null,
      ]
    : row.report_type === 'Report Late'
      // The day as well as the time: Late lists every date, so "Arrived
      // 09:41" alone doesn't say when (lateAt — the corrected date if edited).
      ? [`Late ${fmtDate(lateAt(row))}`, row.employee_time_of_arrival ? `Arrived ${row.employee_time_of_arrival}` : null]
      : [];
  // originalRecord may be missing if it's fallen outside listSubmissions'
  // own 200-row window — the reason still shows either way, just without
  // the "from ..." context in that rare case.
  const changedNote = row.amendment_reason
    ? `Changed: ${row.amendment_reason}${originalRecord ? ` (was: report filed ${fmtDate(originalRecord.submitted_at)})` : ''}`
    : null;
  // This row itself needs nothing further (dashboard_acknowledged is
  // force-set on it by amendSubmission the moment it's superseded), it's
  // purely pointing HR at the row that does.
  const supersededNote = row.superseded_at
    ? `Edited — see the new record${amendedIntoRecord ? ` filed ${fmtDate(amendedIntoRecord.submitted_at)}` : ''}`
    : null;
  const canAmend = isAmendable(row);
  // A filed record opens straight into the editor on click — no separate
  // Edit button/column. Any row with nothing to amend stays a plain
  // (non-clickable) block.
  //
  // Checking for a live text selection first is what lets clicking and
  // copying live side by side: a real click opens the editor, a
  // click-drag-release to select text does not.
  function openEditor() {
    if (window.getSelection().toString()) return;
    onEdit(row);
  }
  const nameBlockProps = canAmend && onEdit
    ? {
        role: 'button', tabIndex: 0, onClick: openEditor,
        onKeyDown: (e) => { if (e.key === 'Enter') openEditor(); },
        'aria-label': `Edit ${row.employee_email || 'this'} record`,
      }
    : {};

  return (
    <div className={`flex items-center gap-1.5 border-t border-gray-100 px-3 py-3.5 first:border-t-0 ${row.superseded_at ? 'bg-red-50/60' : ''}`}>
      {/* Employee, then Category / Dates / Days / Notes as fixed-width
          columns (COLS) under RowListHeader's matching headings, so every
          row's details line up. Never truncated and select-text: this is
          the content HR reads and copies. */}
      <div
        {...nameBlockProps}
        className={`flex min-w-0 flex-1 select-text items-center gap-2${canAmend ? ' cursor-pointer hover:bg-gray-50' : ''}`}
      >
        <div className={COLS.employee}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="whitespace-nowrap text-sm font-medium text-gray-900">{displayName || row.employee_email || '—'}</p>
            {/* Which of the two types this row actually is — mostly for the
                All tab, where Absent and Late rows sit mixed together and the
                icon alone (buried in the Open action) isn't enough to tell
                them apart at a glance. Shown on every row, not just All, so
                it doesn't flicker in and out as the tab changes. Swapped for
                an amber "pending form" badge once return_pending_at is set —
                "Absent" would be actively wrong at that point, they're back,
                just not yet filed. */}
            {/* A closed absence reads "Returned" (green) — the Return to Work
                is filed, it's no longer an open absence. */}
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                pendingReturn
                  ? 'bg-amber-100 text-amber-700'
                  : row.returned_to_work_at
                    ? 'bg-emerald-100 text-emerald-700'
                    : stillAbsentToday ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {pendingReturn
                ? 'Returned — pending form'
                : row.returned_to_work_at ? `Returned ${backAtWorkDate(row)}` : stillAbsentToday ? 'Still absent' : category.label}
            </span>
            {Boolean(row.amended_from_id) && (
              <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                Amended
              </span>
            )}
            {/* The old record stays on screen (never removed — see
                amendSubmission), highlighted red rather than tucked away,
                so it can't be mistaken for something still needing
                action. */}
            {Boolean(row.superseded_at) && (
              <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                Edited
              </span>
            )}
            {/* Who archived it — a supervisor in FSM or HR here; FSM records
                it on the row (archived_by_*), both land in this same section. */}
            {Boolean(row.archived_at) && (
              <span
                className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600"
                title={row.archived_by_email || undefined}
              >
                Archived{row.archived_by_name || row.archived_by_email ? ` by ${row.archived_by_name || row.archived_by_email}` : ''}
              </span>
            )}
          </div>
          <p className="whitespace-normal text-xs text-gray-400">
            {row.engineer_name ? `Reported by ${row.engineer_name}` : 'Reported by —'}
            {' · '}
            {/* Always the date as well as the time, today included (asked
                for 2026-09-28) — a bare time read as "happened today" even
                for an edit to a record from last week. On an amended record
                this is when it was edited, so it says so; when the absence
                itself happened is the "Since" date alongside. */}
            {row.amended_from_id ? 'edited ' : ''}{fmtDate(row.submitted_at)} {fmtTime(row.submitted_at)}
          </p>
          {/* What was changed on an amended record — right under when it
              was edited, not mixed into Notes. */}
          {changedNote && <p className="whitespace-normal text-xs font-medium text-blue-700">{changedNote}</p>}
          {supersededNote && (
            <p className="whitespace-normal text-xs font-medium text-red-700">{supersededNote}</p>
          )}
          {sentNote && <p className="whitespace-normal text-xs font-medium text-blue-700">{sentNote}</p>}
        </div>
        <DetailCells
          category={isAbsence ? row.absence_category : null}
          dateLines={dateLines}
          days={isAbsence ? daysAbsentLabel(row) : null}
          notes={isAbsence ? row.reason_for_absence : row.reason_for_late}
          departmentLines={[row.department_area, row.area]}
          absenceColumns={absenceColumns}
        />
      </div>
      <AcknowledgeButton row={row} acknowledged={acknowledged} onToggle={onToggleAcknowledge} />
      <BrightHrButton row={row} recorded={brightHrRecorded} onToggle={onToggleBrightHr} />
      <AbsenceLateReportButton id={row.id} reportType={row.report_type} onView={onViewReport} />
      <RtwReportButton id={row.id} returnedToWorkAt={row.returned_to_work_at} onView={onViewReport} />
      {/* Whichever of the two applies to this list — Today/All Records pass
          onDiscard (nothing there is archived yet), the Archived section
          passes onUnarchive instead (everything there already is). Never
          both on the same row. */}
      {onUnarchive && row.archived_at ? (
        <button
          type="button"
          onClick={() => onUnarchive(row)}
          title="Unarchive"
          aria-label="Unarchive"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:border-brand hover:text-brand"
        >
          <ArrowUturnUpIcon className="h-4 w-4" />
        </button>
      ) : onDiscard && !row.archived_at ? (
        <button
          type="button"
          onClick={() => onDiscard(row)}
          title="Discard"
          aria-label="Discard"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-600"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ) : (
        <span className="h-9 w-9 shrink-0" aria-hidden="true" />
      )}
    </div>
  );
}

// One "Actions" label spanning the four icon buttons — each used to get its
// own spelled-out column header ("Acknowledge"/"Absence/Late Report"/
// "Return to Work Report"), which stopped fitting once those became
// icon-only (see AbsenceLateReportButton/RtwReportButton/the Acknowledge
// button's own comments); the icons' own title tooltips carry that detail
// now.
function RowListHeader({ absenceColumns = true }) {
  return (
    <div className="flex items-center gap-1.5 border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 first:border-t-0">
      <DetailHeadings absenceColumns={absenceColumns} />
      <span className="w-[15.75rem] shrink-0 text-center">Actions</span>
    </div>
  );
}

// absenceColumns false leaves out Category and Days (the Late list — see DetailCells).
function RowList({
  rows, showDate, namesByEmail, isAcknowledged, onToggleAcknowledge, isBrightHrRecorded, onToggleBrightHr,
  onViewReport, emptyText, submissionsById, amendedByOriginalId, onDiscard, onUnarchive, onEdit, absenceColumns = true,
}) {
  if (!rows.length) {
    return <p className="px-3 py-6 text-center text-sm text-gray-400">{emptyText}</p>;
  }
  const nameCol = nameColumnWidth(
    rows.map((r) => (r.employee_email && namesByEmail.get(r.employee_email.toLowerCase())) || r.employee_email)
  );
  return (
    <div style={{ '--name-col': nameCol, '--days-col': daysColumnWidth(rows.map((r) => (r.report_type === 'Report Absence' ? daysAbsentLabel(r) : ''))) }}>
      <RowListHeader absenceColumns={absenceColumns} />
      {rows.map((r) => (
        <Row
          key={r.id}
          row={r}
          absenceColumns={absenceColumns}
          showDate={showDate}
          displayName={r.employee_email ? namesByEmail.get(r.employee_email.toLowerCase()) : null}
          acknowledged={isAcknowledged(r)}
          onToggleAcknowledge={() => onToggleAcknowledge(r)}
          brightHrRecorded={isBrightHrRecorded(r)}
          onToggleBrightHr={() => onToggleBrightHr(r)}
          onViewReport={onViewReport}
          originalRecord={r.amended_from_id ? submissionsById.get(r.amended_from_id) : null}
          amendedIntoRecord={r.superseded_at ? amendedByOriginalId.get(r.id) : null}
          onDiscard={onDiscard}
          onUnarchive={onUnarchive}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
}

// HR's tick, shared by every section's rows. Icon-only with a tiny label
// under it, so the column only grows a little; title carries the full label.
function AcknowledgeButton({ row, acknowledged, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={acknowledged}
      title={acknowledged ? 'Acknowledged — click to unacknowledge' : 'Not acknowledged — click to acknowledge'}
      aria-label={`Mark ${row.employee_email || 'employee'} as ${acknowledged ? 'not acknowledged' : 'acknowledged'}`}
      className={`flex h-9 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded transition ${
        acknowledged ? 'text-brand' : 'text-gray-300 hover:text-gray-400'
      }`}
    >
      {acknowledged ? <CheckCircleIconSolid className="h-5 w-5 shrink-0" /> : <CheckCircleIcon className="h-5 w-5 shrink-0" />}
      <span className={`text-[9px] font-medium leading-none ${acknowledged ? 'text-brand' : 'text-gray-400'}`}>
        {acknowledged ? 'Acknowledged' : 'Acknowledge'}
      </span>
    </button>
  );
}

// HR's "Recorded in BrightHR" tick, saved on FSM's row as
// brighthr_recorded_at — drawn exactly like AcknowledgeButton (check circle,
// tiny label, brand purple once ticked); title carries the full label. A
// row with nothing filed yet (no numeric id) gets an empty slot of the same
// width so the columns still line up.
function BrightHrButton({ row, recorded, onToggle }) {
  if (typeof row.id !== 'number') return <span className="h-9 w-14 shrink-0" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={recorded}
      title={recorded ? 'Recorded in BrightHR — click to untick' : 'Not recorded in BrightHR — click once recorded'}
      aria-label={`Mark ${row.employee_email || 'employee'} as ${recorded ? 'not recorded' : 'recorded'} in BrightHR`}
      className={`flex h-9 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded transition ${
        recorded ? 'text-brand' : 'text-gray-300 hover:text-gray-400'
      }`}
    >
      {recorded ? <CheckCircleIconSolid className="h-5 w-5 shrink-0" /> : <CheckCircleIcon className="h-5 w-5 shrink-0" />}
      <span className={`text-[9px] font-medium leading-none ${recorded ? 'text-brand' : 'text-gray-400'}`}>
        {recorded ? 'In BrightHR' : 'BrightHR'}
      </span>
    </button>
  );
}

// One open absence waiting on today's answer — still off, or back (see
// isPendingAbsence). No acknowledge tick here: there's nothing to
// acknowledge until someone answers, and HR acts on these by chasing. Once
// answered, the record moves on to Today or Returned, where HR ticks it.
function PendingRow({ row, displayName, onViewReport, onDiscard, onMessage }) {
  const sentNote = messageNote(row);
  return (
    <div className="flex items-center gap-3 border-t border-gray-100 px-3 py-3.5 first:border-t-0">
      {/* Same columns as Row (COLS), under the same headings. */}
      <div className="flex min-w-0 flex-1 select-text items-center gap-2">
        <div className={COLS.employee}>
          <p className="whitespace-nowrap text-sm font-medium text-gray-900">{displayName || row.employee_email}</p>
          {row.engineer_name && <p className="whitespace-normal text-xs text-gray-400">Reported by {row.engineer_name}</p>}
          {sentNote && <p className="whitespace-normal text-xs font-medium text-blue-700">{sentNote}</p>}
        </div>
        <DetailCells
          category={row.absence_category}
          dateLines={[
            leftSiteLine(row),
            `Since ${row.absence_start_date || fmtDate(row.event_at || row.submitted_at)}`,
            row.last_confirmed_at ? `Last confirmed ${fmtDate(row.last_confirmed_at)}` : null,
          ]}
          days={daysAbsentLabel(row)}
          notes={row.reason_for_absence}
        />
      </div>
      <AbsenceLateReportButton id={row.id} reportType="Report Absence" onView={onViewReport} />
      {row.engineer_email ? (
        <button
          type="button"
          onClick={() => onMessage(row)}
          title={row.message_count ? `Conversation with ${row.engineer_name || row.engineer_email} (${row.message_count} sent)` : `Message ${row.engineer_name || row.engineer_email}`}
          aria-label={`Open the conversation with ${row.engineer_name || row.engineer_email}, who reported this absence`}
          className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md border hover:border-brand hover:text-brand ${
            row.message_count ? 'border-brand/40 text-brand' : 'border-gray-200 text-gray-400'
          }`}
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          {row.message_count > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold leading-none text-white">
              {row.message_count}
            </span>
          )}
        </button>
      ) : (
        <span className="h-9 w-9 shrink-0" aria-hidden="true" />
      )}
      <button
        type="button"
        onClick={() => onDiscard(row)}
        title="Discard"
        aria-label="Discard"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:border-red-400 hover:text-red-600"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

// What a chase can ask for, per kind of Pending row — picked in the
// dialog, so the request isn't fixed. First one is the default.
const CHASE_ASKS = {
  absence: [
    { key: 'update', label: 'Still off or back?', text: 'Can you please let me know if they are still off, or back at work? You can answer with the buttons below.' },
    { key: 'return-to-work', label: 'Complete Return to Work', text: "If they're back, can you please complete their Return to Work?" },
    { key: 'fill-form', label: 'Confirm & fill form', text: 'Can you please confirm and fill in the form?' },
  ],
  late: [
    { key: 'fill-form', label: 'Confirm & fill form', text: 'Can you please confirm and fill in the form?' },
    { key: 'arrival', label: 'Ask arrival time', text: 'Can you please let me know what time they arrived?' },
  ],
};

// The facts of a chase, read straight off the record being messaged about
// (target.record — the FSM submission) rather than typed in: who, what
// kind of record, how long, and who reported it.
function chaseFacts(target, employeeName) {
  const r = target.record || {};
  const what = target.kind === 'late' ? 'late' : 'absence';
  const days = target.kind === 'absence' ? daysAbsentLabel(r) : null;
  const since = target.kind === 'absence'
    ? ` (absent since ${r.absence_start_date || fmtDate(r.event_at || r.submitted_at)}${days ? `, ${days}` : ''})`
    : '';
  const reportedBy = r.engineer_name ? `, reported by ${r.engineer_name}` : '';
  return `There is a pending ${what} for ${employeeName}${since}${reportedBy}.`;
}

function chaseTemplate(target, employeeName, recipientName, askKey) {
  const firstName = (recipientName || '').split(' ')[0];
  const asks = CHASE_ASKS[target.kind] || [];
  const ask = asks.find((a) => a.key === askKey) || asks[0];
  return `Hi${firstName ? ` ${firstName}` : ''},\n\n${chaseFacts(target, employeeName)}${ask ? ` ${ask.text}` : ''}\n\nThanks`;
}

// What each button in a chase message did, once it was used (FSM records it
// as the answer to that message — see its recordMessageResponse).
const CHASE_ANSWERS = {
  still_absent: ['Still Absent', (name) => `confirmed ${name} is still absent.`],
  return_to_work: ['Return to Work', () => 'completed the Return to Work.'],
  report_absence: ['Report Absence', () => 'filed a new absence report.'],
  report_late: ['Report Late', () => 'filed a late report.'],
};

// The whole conversation about one record, as a chat: what HR sent (right,
// purple — the full text, who sent it, how and when) and what the person who
// reported it has done (left, grey), oldest at the top. They answer with the
// buttons in the message rather than by replying, so their side is the
// button they picked on each message (only when they used one), plus what
// else the record shows: when it was reported, the latest "still absent",
// "back, Return to Work to follow" and the Return to Work being filed, where
// that wasn't already an answer to a message. Only what they did after HR
// first messaged them: before that there was no conversation, so a record
// nobody has chased yet shows an empty chat. An emailed reply goes to the
// sender's own mailbox, so it can't be shown here.
function chaseThread(target, employeeName, messages) {
  const r = target.record || {};
  const items = [];
  const answered = new Set();
  for (const m of messages || []) {
    items.push({
      key: `m${m.id}`,
      at: m.sentAt,
      side: 'hr',
      text: m.message,
      meta: `${m.senderName || m.senderEmail} · ${m.channel === 'email' ? 'Email' : 'Nano message'}`,
    });
    for (const a of m.responses || []) {
      const [label, did] = CHASE_ANSWERS[a.action] || [a.action, () => ''];
      answered.add(a.action);
      items.push({
        key: `a${m.id}-${a.action}-${a.respondedAt}`,
        at: a.respondedAt,
        side: 'them',
        who: a.responderName || a.responderEmail,
        picked: label,
        text: `Picked “${label}” and ${did(employeeName)}`,
        // the start of the message it answers, like a quoted reply
        quote: m.message.replace(/s+/g, ' ').slice(0, 70) + (m.message.length > 70 ? '…' : ''),
      });
    }
  }
  const event = (key, at, text) => {
    if (at) items.push({ key, at, side: 'them', text });
  };
  const what = target.kind === 'late' ? 'late' : 'absent';
  event('reported', r.submitted_at, `Reported ${employeeName} ${what}${r.absence_category ? ` (${r.absence_category})` : ''}.`);
  if (!answered.has('still_absent')) event('confirmed', r.last_confirmed_at, `Confirmed ${employeeName} is still absent.`);
  if (!answered.has('return_to_work')) {
    event('pending', r.return_pending_at, `Said ${employeeName} is back — Return to Work to follow.`);
    event('rtw', r.returned_to_work_at, 'Filed the Return to Work.');
  }
  const sent = (messages || []).map((m) => new Date(m.sentAt).getTime());
  if (!sent.length) return [];
  const firstSent = Math.min(...sent);
  return items
    .filter((i) => i.side === 'hr' || new Date(i.at).getTime() >= firstSent)
    .sort((a, b) => new Date(a.at) - new Date(b.at));
}

const initials = (name) => (name || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

// Chase someone about a Pending row — as a Nano inbox message (FSM's
// Messages screen + push, under HR) or an email. FSM sends it to whoever
// reported the record (see its absenceMessagesController.js), adds the
// Still Absent / Return to Work / Report Absence / Report Late links, and
// logs it (absence_reporting_messages). Opens on the conversation so far
// and stays open after sending, so the new message lands in the thread.
function MessageSubmitterModal({ target, employeeName, onClose, onSent }) {
  const { data: history, isLoading: historyLoading } = useSubmitterMessages(target.id, true);
  const sendMessage = useSendSubmitterMessage();
  const recipient = history?.recipient;
  const recipientName = recipient?.name || recipient?.email;
  // Email by default — it reaches everyone, with or without a Nano account.
  const [channel, setChannel] = useState('email');
  const asks = CHASE_ASKS[target.kind] || [];
  const [askKey, setAskKey] = useState(asks[0]?.key);
  // null until edited, so the template picks up the recipient's first name
  // once it has loaded instead of being frozen at "Hi,". Picking a
  // different request rebuilds it (and drops any hand edits). '' after a
  // send: an empty box, like any chat, with the template one click away.
  const [edited, setEdited] = useState(null);
  const message = edited ?? chaseTemplate(target, employeeName, recipient?.name, askKey);
  const thread = useMemo(
    () => chaseThread(target, employeeName, history?.messages),
    [target, employeeName, history?.messages]
  );
  const bottom = useRef(null);
  // Newest at the bottom, in view — on opening and when a message lands.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [thread.length]);

  function handleSend() {
    sendMessage.mutate(
      { id: target.id, channel, message, employeeName },
      {
        onSuccess: () => {
          setEdited('');
          if (onSent) onSent(`${channel === 'email' ? 'Email sent' : 'Nano message sent'} to ${recipientName}.`);
        },
      }
    );
  }

  const sendError = sendMessage.error?.response?.data?.error || (sendMessage.isError ? 'Could not send — please try again.' : null);
  let lastDay = null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-light text-xs font-bold text-brand">
            {historyLoading ? '' : initials(recipientName)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">
              {historyLoading ? 'Finding who to send this to...' : recipientName || 'Nobody on file'}
            </p>
            <p className="truncate text-xs text-gray-500">
              {recipient
                ? `Reported ${employeeName}${recipient.name ? ` · ${recipient.email}` : ''}`
                : historyLoading ? '' : `Nobody on file to message about ${employeeName}.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto bg-gray-50 px-4 py-4">
          {historyLoading && <p className="text-center text-xs text-gray-400">Loading the conversation...</p>}
          {!historyLoading && !history?.messages?.length && (
            <p className="rounded-md bg-white px-3 py-2 text-center text-xs text-gray-500">
              No messages sent yet — write the first one below.
            </p>
          )}
          {thread.map((item) => {
            const day = fmtDate(item.at);
            const showDay = day !== lastDay;
            lastDay = day;
            const hr = item.side === 'hr';
            return (
              <div key={item.key}>
                {showDay && <p className="py-1 text-center text-[11px] font-medium text-gray-400">{day}</p>}
                <div className={`flex ${hr ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${
                    hr ? 'rounded-br-sm bg-brand text-white' : 'rounded-bl-sm border border-gray-200 bg-white text-gray-800'
                  }`}
                  >
                    {!hr && <p className="mb-0.5 text-[11px] font-semibold text-brand">{item.who || recipientName || 'Reporter'}</p>}
                    {item.quote && (
                      <p className="mb-1 border-l-2 border-brand/40 pl-2 text-[11px] text-gray-500">{item.quote}</p>
                    )}
                    {item.picked ? (
                      <p className="text-sm">
                        <span className="mr-1 inline-block rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold text-white">{item.picked}</span>
                        <span className="text-gray-700">{item.text.replace(/^Picked “[^”]+” and /, '')}</span>
                      </p>
                    ) : (
                      <p className={`whitespace-pre-line text-sm ${hr ? '' : 'italic'}`}>{item.text}</p>
                    )}
                    <p className={`mt-1 text-right text-[10px] ${hr ? 'text-white/70' : 'text-gray-400'}`}>
                      {hr ? `${item.meta} · ` : ''}{fmtTime(item.at)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          {!historyLoading && history?.messages?.some((m) => m.channel === 'email') && (
            <p className="pt-1 text-center text-[11px] text-gray-400">Replies to an email go to the sender&apos;s own mailbox.</p>
          )}
          <div ref={bottom} />
        </div>

        <div className="space-y-2 border-t border-gray-100 px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { value: 'email', label: 'Email' },
              { value: 'inbox', label: 'Nano message' },
            ].map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setChannel(o.value)}
                className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${
                  channel === o.value ? 'border-brand bg-brand text-white' : 'border-gray-300 text-gray-600 hover:border-brand hover:text-brand'
                }`}
              >
                {o.label}
              </button>
            ))}
            {asks.length > 1 && <span className="mx-1 h-4 w-px bg-gray-200" aria-hidden="true" />}
            {asks.length > 1 && asks.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => { setAskKey(a.key); setEdited(null); }}
                title="Start the message from this request"
                className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                  askKey === a.key && edited === null ? 'border-brand bg-brand-light text-brand' : 'border-gray-300 text-gray-600 hover:border-brand hover:text-brand'
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              value={message}
              onChange={(e) => setEdited(e.target.value)}
              rows={message ? 7 : 3}
              maxLength={2000}
              placeholder={asks.length ? 'Write a message, or pick a request above to start from it' : 'Write a message'}
              className="min-w-0 flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-brand"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={sendMessage.isPending || !message.trim() || !recipient}
              className="shrink-0 rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
            >
              {sendMessage.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>
          {sendError && <p className="text-xs text-rose-700">{sendError}</p>}
        </div>
      </div>
    </div>
  );
}

function PendingList({ rows, namesByEmail, onViewReport, onDiscard, onMessage }) {
  if (!rows.length) {
    return <p className="px-3 py-6 text-center text-sm text-gray-400">No one waiting on an update — every open absence is confirmed for today.</p>;
  }
  const nameCol = nameColumnWidth(rows.map((r) => namesByEmail.get((r.employee_email || '').toLowerCase()) || r.employee_email));
  return (
    <div style={{ '--name-col': nameCol, '--days-col': daysColumnWidth(rows.map((r) => daysAbsentLabel(r))) }}>
      <div className="flex items-center gap-3 border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        <DetailHeadings />
        <span className="w-9 shrink-0 text-center">Report</span>
        <span className="w-9 shrink-0 text-center">Chase</span>
        <span className="w-9 shrink-0 text-center">Discard</span>
      </div>
      {rows.map((r) => (
        <PendingRow
          key={r.id}
          row={r}
          displayName={namesByEmail.get((r.employee_email || '').toLowerCase())}
          onViewReport={onViewReport}
          onDiscard={onDiscard}
          onMessage={onMessage}
        />
      ))}
    </div>
  );
}

function AbsenceReportingDashboard() {
  const { data: submissions, isLoading, error } = useAbsenceReportingSubmissions();
  const { data: directory } = useDirectory();
  const updateSubmission = useUpdateAbsenceReportingSubmission();
  const archiveSubmission = useArchiveAbsenceReportingSubmission();
  const unarchiveSubmission = useUnarchiveAbsenceReportingSubmission();
  const queryClient = useQueryClient();
  // Discard archives the one record, and every section here (and FSM's own
  // list) reads the same records and leaves archived ones out — so it goes
  // from all of them at once and lands in Archived.
  function handleDiscard(row) {
    if (!window.confirm('Discard this record? It stays available in Archived — nothing is deleted.')) return;
    archiveSubmission.mutate(row.id, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] }),
    });
  }
  function handleUnarchive(row) {
    unarchiveSubmission.mutate(row.id, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] }),
    });
  }
  // Pending/Today/Returned/Late default open — they're what needs attention.
  // Archived defaults closed: a discarded record needs nothing further from
  // anyone, so it shouldn't compete for screen space with the ones that do.
  const [openSections, setOpenSections] = useState({ pending: true, today: true, returned: true, late: true, archived: false });

  const [todayType, setTodayType] = useState(ALL_TYPE);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Independent per gallery — narrowing one shouldn't touch the others.
  const [pendingEmployeeFilter, setPendingEmployeeFilter] = useState('');
  const [todayEmployeeFilter, setTodayEmployeeFilter] = useState('');
  const [returnedEmployeeFilter, setReturnedEmployeeFilter] = useState('');
  // "Acknowledged only" per panel — independent of each other and of
  // Today's Absent/Late tabs. 'all' | 'acknowledged' | 'not-acknowledged'.
  const [todayAckFilter, setTodayAckFilter] = useState('all');
  const [returnedAckFilter, setReturnedAckFilter] = useState('all');
  // Late's own filters — by the day they were late, not touching Returned's.
  const [lateEmployeeFilter, setLateEmployeeFilter] = useState('');
  const [lateAckFilter, setLateAckFilter] = useState('all');
  const [lateFrom, setLateFrom] = useState('');
  const [lateTo, setLateTo] = useState('');
  // Optimistic local ticks, keyed by id — { acknowledged, at } — for instant
  // click feedback rather than waiting on the PUT + refetch. Dropped once the
  // refetch brings the saved dashboard_acknowledged(_at) back.
  const [ackOverrides, setAckOverrides] = useState({});
  // The BrightHR tick as just clicked, until FSM's list comes back with it.
  const [brightHrOverrides, setBrightHrOverrides] = useState({});
  // The document currently open in the in-app viewer — a `{ source:
  // 'api-fetch', url, title }` block DocumentViewer.jsx knows how to fetch
  // as authenticated bytes (see its own comment), same pattern FSM's
  // AbsenceReportingList.jsx uses for these same report PDFs.
  const [viewerBlock, setViewerBlock] = useState(null);
  // The Pending row being chased (MessageSubmitterModal) — see
  // chaseTemplate for the shape.
  const [messageTarget, setMessageTarget] = useState(null);
  // The success pop-up's text once a chase message has gone, else null.
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);
  // The record open in AbsenceRecordEditor.jsx.
  const [editingId, setEditingId] = useState(null);
  const openReport = useCallback((url, title) => setViewerBlock({ source: 'api-fetch', url, title }), []);

  // Whether HR's tick still stands: a tick made before the record's latest
  // Still Absent (or before it was filed/edited) is stale, so it needs
  // acknowledging again on Today. A return never unticks it. A tick saved before
  // dashboard_acknowledged_at existed has no time on it, so it just counts.
  const isAcknowledged = useCallback(
    (row) => {
      const override = ackOverrides[row.id];
      const acknowledged = override ? override.acknowledged : row.dashboard_acknowledged === 'acknowledged';
      if (!acknowledged) return false;
      const at = override ? override.at : row.dashboard_acknowledged_at ? new Date(row.dashboard_acknowledged_at).getTime() : null;
      if (at === null) return true;
      return at >= lastStatusChange(row);
    },
    [ackOverrides]
  );

  // Today/Pending/Returned export row — one shape covering both Report
  // Absence's and Report Late's own start-date/arrival-time and
  // notes/reason fields, so a mixed-type export still reads as one table.
  // Same order as SUBMISSION_EXPORT_COLUMNS — every date and time in its own
  // column, each saying what it is (see the Column guide sheet).
  function submissionExportRow(row) {
    return [
      namesByEmail.get((row.employee_email || '').toLowerCase()) || row.employee_email || '',
      row.employee_email || '',
      row.department_area || '',
      row.area || '',
      REPORT_TYPE_LABEL[row.report_type] || row.report_type,
      absenceDateText(row),
      row.report_type === 'Report Absence'
        ? row.time_employee_left_site || 'Absent from start of day'
        : '',
      row.report_type === 'Report Late' ? row.employee_time_of_arrival || '' : '',
      returnedDateText(row),
      daysAbsentText(row),
      row.reason_for_absence || row.reason_for_late || '',
      row.engineer_name || '',
      row.engineer_email || '',
      xDate(row.submitted_at),
      row.amended_from_id ? 'Yes' : 'No',
      xDate(row.returned_to_work_at),
      isAcknowledged(row) ? 'Yes' : 'No',
      isBrightHrRecorded(row) ? 'Yes' : 'No',
    ];
  }

  // Unlike Acknowledged, nothing unticks it — not a Still Absent, a return or
  // an edit. Once recorded in BrightHR it stays ticked until someone unticks
  // it by hand.
  function isBrightHrRecorded(row) {
    return row.id in brightHrOverrides ? brightHrOverrides[row.id] : Boolean(row.brighthr_recorded_at);
  }

  function toggleBrightHr(row) {
    const next = !isBrightHrRecorded(row);
    setBrightHrOverrides((prev) => ({ ...prev, [row.id]: next }));
    updateSubmission.mutate(
      { id: row.id, brightHrRecorded: next },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] });
          setBrightHrOverrides(({ [row.id]: _done, ...rest }) => rest);
        },
      }
    );
  }

  function toggleAcknowledge(row) {
    const next = isAcknowledged(row) ? null : 'acknowledged';
    setAckOverrides((prev) => ({ ...prev, [row.id]: { acknowledged: Boolean(next), at: Date.now() } }));
    updateSubmission.mutate(
      { id: row.id, dashboardAcknowledged: next },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({ queryKey: ['absence-reporting-submissions'] });
          setAckOverrides(({ [row.id]: _done, ...rest }) => rest);
        },
      }
    );
  }

  const namesByEmail = useMemo(() => {
    const map = new Map();
    for (const p of directory?.people || []) {
      if (p.email) map.set(p.email.toLowerCase(), p.display_name);
    }
    return map;
  }, [directory]);

  // Cross-references between an amended row and the original it replaced —
  // both directions, since a row on screen could be either end of the pair
  // (amendment_reason/amended_from_id from Row's own perspective vs.
  // amendedByOriginalId, the reverse lookup a superseded row needs to say
  // what replaced it). Both apps' amendSubmission only ever produces one
  // amended-by per original, so the reverse map is 1:1 in practice.
  const submissionsById = useMemo(() => {
    const map = new Map();
    for (const r of submissions || []) map.set(r.id, r);
    return map;
  }, [submissions]);
  const amendedByOriginalId = useMemo(() => {
    const map = new Map();
    for (const r of submissions || []) {
      if (r.amended_from_id) map.set(r.amended_from_id, r);
    }
    return map;
  }, [submissions]);

  // Everyone in the company directory (the users lookup) — see
  // employeeOptionsFromDirectory for leavers still on a record.
  const employeeOptions = useMemo(
    () => employeeOptionsFromDirectory(directory, (submissions || []).map((r) => r.employee_email)),
    [directory, submissions]
  );

  const matchesEmployee = (row, filter) => !filter || (row.employee_email || '').toLowerCase() === filter;

  // Open absences nobody has confirmed today (see isPendingAbsence) — HR is
  // waiting to hear whether they're still off or back. Longest-open first.
  const pendingRows = useMemo(
    () =>
      (submissions || [])
        .filter((r) => isPendingAbsence(r) && matchesEmployee(r, pendingEmployeeFilter))
        .sort((a, b) => new Date(a.event_at || a.submitted_at) - new Date(b.event_at || b.submitted_at)),
    [submissions, pendingEmployeeFilter]
  );

  // Today: absences and lateness reported or confirmed today. Anyone back at
  // work — even if they came back today — is in Returned instead.
  const todayRows = useMemo(
    () =>
      (submissions || []).filter(
        (r) =>
          matchesType(r, todayType)
          && !isReturned(r)
          && isToday(todayAnchor(r))
          && !isDismissedLate(r)
          && !r.archived_at
          && matchesEmployee(r, todayEmployeeFilter)
          && matchesAck(r, todayAckFilter, isAcknowledged)
      ),
    [submissions, todayType, todayAckFilter, todayEmployeeFilter, isAcknowledged]
  );

  // Returned: every absence that's come back — the Return to Work filed, or
  // a manager has said they're back and the form is to follow. Newest
  // return first; the date filter goes by when they came back.
  const returnedRows = useMemo(
    () =>
      (submissions || [])
        .filter(
          (r) =>
            isReturned(r)
            && !r.archived_at
            && (fromDate || toDate ? inDateRange(returnedAt(r), fromDate, toDate) : true)
            && matchesEmployee(r, returnedEmployeeFilter)
            && matchesAck(r, returnedAckFilter, isAcknowledged)
        )
        .sort((a, b) => new Date(returnedAt(b)) - new Date(returnedAt(a))),
    [submissions, fromDate, toDate, returnedEmployeeFilter, returnedAckFilter, isAcknowledged]
  );

  // Late: every lateness from before today. Today's stay under Today and
  // move here the next day — each late report is in one place at a time,
  // as an absence is (Pending/Today/Returned). "Today's" is exactly what
  // Today shows (reported today, todayAnchor), so nothing falls between
  // the two. Leaves out what an engineer Dismissed on FSM as not genuine
  // (as Today does) and anything discarded (that's under Archived).
  // Newest first.
  const lateRows = useMemo(
    () =>
      (submissions || [])
        .filter(
          (r) =>
            r.report_type === 'Report Late'
            && !isToday(todayAnchor(r))
            && !isDismissedLate(r)
            && !r.archived_at
            && (lateFrom || lateTo ? inDateRange(lateAt(r), lateFrom, lateTo) : true)
            && matchesEmployee(r, lateEmployeeFilter)
            && matchesAck(r, lateAckFilter, isAcknowledged)
        )
        .sort((a, b) => new Date(lateAt(b)) - new Date(lateAt(a))),
    [submissions, lateFrom, lateTo, lateEmployeeFilter, lateAckFilter, isAcknowledged]
  );

  // Discarded (see handleDiscard/archiveSubmission) — never deleted, kept
  // visible here instead, collapsed by default (see openSections' own
  // archived: false).
  const archivedRows = useMemo(
    () => (submissions || [])
      .filter((r) => r.archived_at)
      .sort((a, b) => new Date(b.archived_at) - new Date(a.archived_at)),
    [submissions]
  );

  const todayEmptyText = todayAckFilter !== 'all'
    ? `No ${todayAckFilter === 'acknowledged' ? 'acknowledged' : 'unacknowledged'} reports today.`
    : todayType === ALL_TYPE
      ? 'No one absent or late today.'
      : TODAY_EMPTY_TEXT[todayType];
  const returnedEmptyText = returnedAckFilter !== 'all'
    ? `No ${returnedAckFilter === 'acknowledged' ? 'acknowledged' : 'unacknowledged'} returns for this filter.`
    : 'No returns for this filter.';
  const lateEmptyText = lateAckFilter !== 'all'
    ? `No ${lateAckFilter === 'acknowledged' ? 'acknowledged' : 'unacknowledged'} late records for this filter.`
    : lateFrom || lateTo || lateEmployeeFilter ? 'No late records for this filter.' : 'No late records before today.';

  return (
    <div className="p-4 sm:p-8">
      <Link
        to="/?dept=hr"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back to HR
      </Link>

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Absence & Late Reporting</h1>
        <p className="mt-1 text-sm text-gray-500">
          Who's out today, who's returned, and the full record of absence and lateness.
        </p>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading submissions...</p>}
      {error && (
        <p className="text-sm text-rose-700">
          Could not load submissions from FSM: {error.response?.data?.error || error.message}
        </p>
      )}

      {!isLoading && !error && (
        <div className="flex flex-col gap-6">
          {/* First, not last — an open absence with nothing closing it out
              is the thing most likely to need chasing, ahead of both
              "what's already been reported today" and the full history. */}
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className={`flex items-start justify-between gap-3 px-3 py-3 ${openSections.pending ? 'border-b border-gray-100' : ''}`}>
              <SectionToggle
                title="Pending"
                description="Open absences with no update today — still off, or back?"
                open={openSections.pending}
                onToggle={() => setOpenSections((prev) => ({ ...prev, pending: !prev.pending }))}
              />
              {/* Filter/export only while open — same as Today/Returned. */}
              {openSections.pending && (
                <div className="flex shrink-0 items-center gap-2">
                  <EmployeeLookup value={pendingEmployeeFilter} onChange={setPendingEmployeeFilter} options={employeeOptions} placeholder="All employees" />
                  <ExportButton
                    onClick={() => exportToExcel(
                      exportFileName('pending'),
                      'Pending',
                      // Same columns as Today/Returned — every export has the same dates.
                      SUBMISSION_EXPORT_COLUMNS,
                      pendingRows.map((r) => submissionExportRow(r))
                    )}
                  />
                </div>
              )}
            </div>
            {openSections.pending && (
              <PendingList
                rows={pendingRows}
                namesByEmail={namesByEmail}
                onViewReport={openReport}
                onDiscard={handleDiscard}
                onMessage={(r) => setMessageTarget({ id: r.id, kind: 'absence', employeeEmail: r.employee_email, record: r })}
              />
            )}
          </section>

          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className={`px-3 py-3 ${openSections.today ? 'border-b border-gray-100' : ''}`}>
              <SectionToggle
                title="Today"
                description="Absent or late today. Anyone back at work is under Returned."
                open={openSections.today}
                onToggle={() => setOpenSections((prev) => ({ ...prev, today: !prev.today }))}
              />
            </div>
            {openSections.today && (
              <>
              <div className="flex items-center justify-between border-b border-gray-100 px-3 pt-3">
                <TypeTabs active={todayType} onChange={setTodayType} />
                <div className="flex items-center gap-3 pb-2">
                  <select
                    value={todayAckFilter}
                    onChange={(e) => setTodayAckFilter(e.target.value)}
                    className="rounded-md border border-gray-300 py-1 pl-2 pr-6 text-xs font-medium text-gray-600 focus:border-brand focus:ring-brand"
                  >
                    {ACK_FILTER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <EmployeeLookup value={todayEmployeeFilter} onChange={setTodayEmployeeFilter} options={employeeOptions} placeholder="All employees" />
                  <ExportButton
                    onClick={() => exportToExcel(
                      exportFileName('today'),
                      'Today',
                      SUBMISSION_EXPORT_COLUMNS,
                      todayRows.map((r) => submissionExportRow(r))
                    )}
                  />
                </div>
              </div>
                <RowList
                  onEdit={(r) => setEditingId(r.id)}
                  rows={todayRows}
                  showDate={false}
                  namesByEmail={namesByEmail}
                  isAcknowledged={isAcknowledged}
                  onToggleAcknowledge={toggleAcknowledge}
                  isBrightHrRecorded={isBrightHrRecorded}
                  onToggleBrightHr={toggleBrightHr}
                  onViewReport={openReport}
                  onDiscard={handleDiscard}
                  emptyText={todayEmptyText}
                  submissionsById={submissionsById}
                  amendedByOriginalId={amendedByOriginalId}
                />
              </>
            )}
          </section>

          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className={`px-3 py-3 ${openSections.returned ? 'border-b border-gray-100' : ''}`}>
              <SectionToggle
                title="Returned"
                description="Everyone back from an absence — every date, including today. The dates filter by when they came back."
                open={openSections.returned}
                onToggle={() => setOpenSections((prev) => ({ ...prev, returned: !prev.returned }))}
              />
            </div>
            {openSections.returned && (
              <>
              <div className="flex flex-wrap items-center justify-end gap-3 border-b border-gray-100 px-3 py-3">
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <select
                    value={returnedAckFilter}
                    onChange={(e) => setReturnedAckFilter(e.target.value)}
                    className="rounded-md border border-gray-300 py-1 pl-2 pr-6 text-xs font-medium text-gray-600 focus:border-brand focus:ring-brand"
                  >
                    {ACK_FILTER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <EmployeeLookup value={returnedEmployeeFilter} onChange={setReturnedEmployeeFilter} options={employeeOptions} placeholder="All employees" />
                  <ExportButton
                    onClick={() => exportToExcel(
                      exportFileName('returned'),
                      'Returned',
                      SUBMISSION_EXPORT_COLUMNS,
                      returnedRows.map((r) => submissionExportRow(r))
                    )}
                  />
                  <label className="flex items-center gap-1">
                    From
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    To
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </label>
                  {(fromDate || toDate) && (
                    <button
                      type="button"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                      }}
                      className="text-brand hover:text-brand-dark"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
                <RowList
                  onEdit={(r) => setEditingId(r.id)}
                  rows={returnedRows}
                  showDate
                  namesByEmail={namesByEmail}
                  isAcknowledged={isAcknowledged}
                  onToggleAcknowledge={toggleAcknowledge}
                  isBrightHrRecorded={isBrightHrRecorded}
                  onToggleBrightHr={toggleBrightHr}
                  onViewReport={openReport}
                  onDiscard={handleDiscard}
                  emptyText={returnedEmptyText}
                  submissionsById={submissionsById}
                  amendedByOriginalId={amendedByOriginalId}
                />
              </>
            )}
          </section>

          {/* Every late record, any date — the same row and controls as
              Returned, its dates by the day they were late. */}
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className={`px-3 py-3 ${openSections.late ? 'border-b border-gray-100' : ''}`}>
              <SectionToggle
                title="Late"
                description="Every late record before today — today's are under Today. Newest first; the dates filter by the day they were late."
                open={openSections.late}
                onToggle={() => setOpenSections((prev) => ({ ...prev, late: !prev.late }))}
              />
            </div>
            {openSections.late && (
              <>
              <div className="flex flex-wrap items-center justify-end gap-3 border-b border-gray-100 px-3 py-3">
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <select
                    value={lateAckFilter}
                    onChange={(e) => setLateAckFilter(e.target.value)}
                    className="rounded-md border border-gray-300 py-1 pl-2 pr-6 text-xs font-medium text-gray-600 focus:border-brand focus:ring-brand"
                  >
                    {ACK_FILTER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <EmployeeLookup value={lateEmployeeFilter} onChange={setLateEmployeeFilter} options={employeeOptions} placeholder="All employees" />
                  <ExportButton
                    onClick={() => exportToExcel(
                      exportFileName('late'),
                      'Late',
                      SUBMISSION_EXPORT_COLUMNS,
                      lateRows.map((r) => submissionExportRow(r))
                    )}
                  />
                  <label className="flex items-center gap-1">
                    From
                    <input
                      type="date"
                      value={lateFrom}
                      onChange={(e) => setLateFrom(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    To
                    <input
                      type="date"
                      value={lateTo}
                      onChange={(e) => setLateTo(e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    />
                  </label>
                  {(lateFrom || lateTo) && (
                    <button
                      type="button"
                      onClick={() => {
                        setLateFrom('');
                        setLateTo('');
                      }}
                      className="text-brand hover:text-brand-dark"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
                <RowList
                  onEdit={(r) => setEditingId(r.id)}
                  rows={lateRows}
                  absenceColumns={false}
                  showDate
                  namesByEmail={namesByEmail}
                  isAcknowledged={isAcknowledged}
                  onToggleAcknowledge={toggleAcknowledge}
                  isBrightHrRecorded={isBrightHrRecorded}
                  onToggleBrightHr={toggleBrightHr}
                  onViewReport={openReport}
                  onDiscard={handleDiscard}
                  emptyText={lateEmptyText}
                  submissionsById={submissionsById}
                  amendedByOriginalId={amendedByOriginalId}
                />
              </>
            )}
          </section>

          {/* Closed by default (openSections' own archived: false) — a
              discarded record needs nothing further from anyone, so it
              shouldn't compete for attention with the sections above,
              which default open. No filters here (archivedRows is already
              small and specific), so this can use Collapsible's own
              all-in-one header instead of a bare SectionToggle beside
              filter controls like the others. */}
          <Collapsible
            title="Archived"
            description="Discarded records — kept, not deleted, and never counted in reporting."
            badge={String(archivedRows.length)}
            open={openSections.archived}
            onToggle={() => setOpenSections((prev) => ({ ...prev, archived: !prev.archived }))}
          >
            <RowList
              rows={archivedRows}
              showDate
              namesByEmail={namesByEmail}
              isAcknowledged={isAcknowledged}
              onToggleAcknowledge={toggleAcknowledge}
              isBrightHrRecorded={isBrightHrRecorded}
              onToggleBrightHr={toggleBrightHr}
              onViewReport={openReport}
              onUnarchive={handleUnarchive}
              emptyText="Nothing discarded."
              submissionsById={submissionsById}
              amendedByOriginalId={amendedByOriginalId}
            />
          </Collapsible>
        </div>
      )}

      {viewerBlock && <DocumentViewer block={viewerBlock} onClose={() => setViewerBlock(null)} />}
      {editingId && <AbsenceRecordEditor id={editingId} onClose={() => setEditingId(null)} />}
      {messageTarget && (
        <MessageSubmitterModal
          target={messageTarget}
          employeeName={namesByEmail.get((messageTarget.employeeEmail || '').toLowerCase()) || messageTarget.employeeEmail}
          onClose={() => setMessageTarget(null)}
          // stays open: the new message shows in the conversation
          onSent={(text) => setToast(text)}
        />
      )}
      {toast && <SuccessToast text={toast} onDone={clearToast} />}
    </div>
  );
}

// A short-lived success pop-up, bottom-right, that clears itself after a
// few seconds (or on click).
function SuccessToast({ text, onDone }) {
  useEffect(() => {
    const id = setTimeout(onDone, 4000);
    return () => clearTimeout(id);
  }, [text, onDone]);
  return (
    <div className="fixed bottom-6 right-6 z-50" role="status" aria-live="polite">
      <button
        type="button"
        onClick={onDone}
        className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-medium text-white shadow-lg"
      >
        <CheckCircleIconSolid className="h-5 w-5 shrink-0" />
        {text}
      </button>
    </div>
  );
}

export default AbsenceReportingDashboard;
